import React, { useState } from "react";

import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { Alert, Box, Divider, Typography, useMediaQuery, useTheme, Stack, IconButton, styled } from "@mui/material";
import ChartModal from "components/modules/DcaBotDetail/ChartHistory/ChartModal";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";

interface IProps {
  title: string;
  children: React.ReactNode;
  loading?: boolean;
  noPaddingBottom?: boolean;
}

const ChartBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "noPaddingBottom",
})<{ noPaddingBottom?: boolean }>(({ theme, noPaddingBottom }) => {
  const { colors } = useMUIThemeModeContext();

  return {
    padding: theme.spacing(2),
    paddingBottom: noPaddingBottom ? 0 : theme.spacing(2),
    borderRadius: "10px",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${colors.gray200}`,
  };
});

const ChartWrapper = ({ title, children, loading, noPaddingBottom }: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [fullScreen, setFullScreen] = useState(false);

  if (!children) {
    return null;
  }

  return (
    <ChartBox noPaddingBottom={noPaddingBottom}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" pb={1}>
        <Typography fontSize={"1rem"} component="h1" fontWeight={600}>
          {title}
        </Typography>
        {!isMobile && children && !loading && (
          <IconButton size="small" onClick={() => setFullScreen(true)}>
            <FullscreenIcon />
          </IconButton>
        )}
      </Stack>
      <Divider sx={{ mb: 1 }} />
      <Box alignItems="center">
        <ErrorBoundary FallbackComponent={(<Alert severity="error">{t("chartHistory.error")}</Alert>) as any}>
          {children}
        </ErrorBoundary>
      </Box>
      {fullScreen && (
        <ChartModal title={title} handleClose={() => setFullScreen(false)}>
          <ErrorBoundary FallbackComponent={(<Alert severity="error">{t("chartHistory.error")}</Alert>) as any}>
            {React.isValidElement(children) ? React.cloneElement(children, { isFullScreen: true } as any) : children}
          </ErrorBoundary>
        </ChartModal>
      )}
    </ChartBox>
  );
};

export default ChartWrapper;
